import type { Theme, Components } from "@mui/material/styles";

import { varAlpha } from "minimal-shared/utils";

import { inputBaseClasses } from "@mui/material/InputBase";
import { filledInputClasses } from "@mui/material/FilledInput";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";

// ----------------------------------------------------------------------

const INPUT_ERROR_BORDER = "#E53E3E";
const INPUT_ERROR_BG = "rgba(229, 62, 62, 0.08)";
const INPUT_ERROR_BG_FOCUSED = "rgba(229, 62, 62, 0.16)";
/** Anel do estado de erro — vermelho sutil, casa com a borda de erro. */
const INPUT_ERROR_RING = "0 0 0 3px rgba(229, 62, 62, 0.14)";

/** Raio dos cantos do input (estética moderna, alinhado ao botão) */
const INPUT_BORDER_RADIUS_PX = 12;
/** Altura mínima do input (48–56px facilita clique/toque) */
const INPUT_MIN_HEIGHT_PX = 56;
/** Padding horizontal interno do input (evita texto encostar nas bordas) */
const INPUT_PADDING_X_PX = 16;

// ----------------------------------------------------------------------

const MuiInputBase: Components<Theme>["MuiInputBase"] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      [`&.${inputBaseClasses.disabled}`]: { "& svg": { color: theme.vars.palette.text.disabled } },
      [`& .${inputBaseClasses.input}:focus`]: { borderRadius: "inherit" },
    }),
    input: ({ theme }) => ({
      fontSize: theme.typography.pxToRem(15),
      [theme.breakpoints.down("sm")]: {
        // This will prevent zoom in Safari min font size ~ 16px
        fontSize: theme.typography.pxToRem(16),
      },
      "&::placeholder": { opacity: 1, color: theme.vars.palette.text.disabled },
      // Sobrescreve o fundo azul do autofill do navegador para manter a paleta do tema
      "&:-webkit-autofill": {
        WebkitBoxShadow: `0 0 0 100px ${theme.vars.palette.background.paper} inset !important`,
        boxShadow: `0 0 0 100px ${theme.vars.palette.background.paper} inset !important`,
        WebkitTextFillColor: `${theme.vars.palette.text.primary} !important`,
        caretColor: theme.vars.palette.text.primary,
      },
      "&:-webkit-autofill:hover": {
        WebkitBoxShadow: `0 0 0 100px ${theme.vars.palette.background.paper} inset !important`,
        boxShadow: `0 0 0 100px ${theme.vars.palette.background.paper} inset !important`,
      },
      "&:-webkit-autofill:focus": {
        WebkitBoxShadow: `0 0 0 100px ${theme.vars.palette.background.paper} inset !important`,
        boxShadow: `0 0 0 100px ${theme.vars.palette.background.paper} inset !important`,
      },
      "&:-webkit-autofill:active": {
        WebkitBoxShadow: `0 0 0 100px ${theme.vars.palette.background.paper} inset !important`,
        boxShadow: `0 0 0 100px ${theme.vars.palette.background.paper} inset !important`,
      },
    }),
  },
};

// ----------------------------------------------------------------------

const MuiInput: Components<Theme>["MuiInput"] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    underline: ({ theme }) => ({
      "&::before": { borderBottomColor: varAlpha(theme.vars.palette.grey["500Channel"], 0.32) },
      "&::after": { borderBottomColor: theme.vars.palette.text.primary },
    }),
  },
};

// ----------------------------------------------------------------------

const MuiOutlinedInput: Components<Theme>["MuiOutlinedInput"] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      minHeight: INPUT_MIN_HEIGHT_PX,
      paddingLeft: INPUT_PADDING_X_PX,
      paddingRight: INPUT_PADDING_X_PX,
      borderRadius: INPUT_BORDER_RADIUS_PX,
      // Vidro fosco = tinta translúcida sobre a superfície. `text.primary`
      // flipa com o modo (idêntico ao branco antigo no dark; escurece de leve
      // no light em vez de clarear invisível).
      backgroundColor: varAlpha(theme.vars.palette.text.primaryChannel, 0.05),
      transition: theme.transitions.create(
        ["border-color", "box-shadow", "background-color"],
        { duration: theme.transitions.duration.short }
      ),
      // Área do adornment (ex.: ícone do olho na senha) com o mesmo fundo do campo — evita faixa de cor diferente
      "& .MuiInputAdornment-root": {
        backgroundColor: "transparent",
      },
      // Quando o input está pré-preenchido (autofill), o box-shadow do input só cobre o elemento input;
      // a direita (adornment) ficava com o fundo do root. Igualamos o fundo do root ao do autofill.
      [`&:has(.${inputBaseClasses.input}:-webkit-autofill)`]: {
        backgroundColor: theme.vars.palette.background.surfaceAlt,
      },
      [`&:has(.${inputBaseClasses.input}:-webkit-autofill).${outlinedInputClasses.focused}`]: {
        backgroundColor: theme.vars.palette.background.paper,
      },
      // Foco NEUTRO, mas INCONFUNDÍVEL (WCAG 2.4.11): borda de cinza forte + um
      // anel visível. A marca (#E35414) segue reservada pro botão — foco laranja
      // competiria com o CTA e colaria no vermelho de erro. O anel antes era 0.18
      // (quase sumia sobre o campo); subimos pra 0.4 (borda 0.8 -> 0.9) pra o
      // "você está aqui" ler de longe. `error` (depois) vence no foco+erro.
      [`&.${outlinedInputClasses.focused}`]: {
        backgroundColor: theme.vars.palette.background.paper,
        boxShadow: `0 0 0 3px ${varAlpha(theme.vars.palette.grey["500Channel"], 0.4)}`,
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: varAlpha(theme.vars.palette.grey["500Channel"], 0.9),
          borderWidth: 1,
        },
      },
      [`&.${outlinedInputClasses.error}`]: {
        backgroundColor: INPUT_ERROR_BG,
        boxShadow: INPUT_ERROR_RING,
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: INPUT_ERROR_BORDER,
        },
      },
      [`&.${outlinedInputClasses.disabled}`]: {
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: theme.vars.palette.action.disabledBackground,
        },
      },
      // Cor do texto e fundo transparente para o input mostrar o mesmo fundo do root (evita faixa diferente ao lado do adornment)
      [`& .${inputBaseClasses.input}`]: {
        color: theme.vars.palette.text.primary,
        backgroundColor: "transparent",
      },
      // Autofill: fundo escuro harmonizado + texto claro + borda laranja sutil; transition "trava" a cor do navegador
      [`& .${inputBaseClasses.input}:-webkit-autofill`]: {
        WebkitTextFillColor: `${theme.vars.palette.text.primary} !important`,
        caretColor: theme.vars.palette.text.primary,
        transition: "background-color 5000s ease-in-out 0s",
        WebkitBoxShadow: `inset 0 0 0 1000px ${theme.vars.palette.background.surfaceAlt}, inset 0 0 0 1px ${varAlpha(theme.vars.palette.primary.mainChannel, 0.2)} !important`,
        boxShadow: `inset 0 0 0 1000px ${theme.vars.palette.background.surfaceAlt}, inset 0 0 0 1px ${varAlpha(theme.vars.palette.primary.mainChannel, 0.2)} !important`,
      },
      [`& .${inputBaseClasses.input}:-webkit-autofill:hover`]: {
        transition: "background-color 5000s ease-in-out 0s",
        WebkitBoxShadow: `inset 0 0 0 1000px ${theme.vars.palette.background.surfaceAlt}, inset 0 0 0 1px ${varAlpha(theme.vars.palette.primary.mainChannel, 0.2)} !important`,
        boxShadow: `inset 0 0 0 1000px ${theme.vars.palette.background.surfaceAlt}, inset 0 0 0 1px ${varAlpha(theme.vars.palette.primary.mainChannel, 0.2)} !important`,
      },
      [`& .${inputBaseClasses.input}:-webkit-autofill:focus`]: {
        WebkitTextFillColor: `${theme.vars.palette.text.primary} !important`,
        caretColor: theme.vars.palette.text.primary,
        transition: "background-color 5000s ease-in-out 0s",
        WebkitBoxShadow: `inset 0 0 0 1000px ${theme.vars.palette.background.paper}, inset 0 0 0 1px ${varAlpha(theme.vars.palette.primary.mainChannel, 0.3)} !important`,
        boxShadow: `inset 0 0 0 1000px ${theme.vars.palette.background.paper}, inset 0 0 0 1px ${varAlpha(theme.vars.palette.primary.mainChannel, 0.3)} !important`,
      },
      [`& .${inputBaseClasses.input}:-webkit-autofill:active`]: {
        transition: "background-color 5000s ease-in-out 0s",
        WebkitBoxShadow: `inset 0 0 0 1000px ${theme.vars.palette.background.surfaceAlt}, inset 0 0 0 1px ${varAlpha(theme.vars.palette.primary.mainChannel, 0.2)} !important`,
        boxShadow: `inset 0 0 0 1000px ${theme.vars.palette.background.surfaceAlt}, inset 0 0 0 1px ${varAlpha(theme.vars.palette.primary.mainChannel, 0.2)} !important`,
      },
    }),
    notchedOutline: ({ theme }) => ({
      borderRadius: INPUT_BORDER_RADIUS_PX,
      borderColor: varAlpha(theme.vars.palette.grey["500Channel"], 0.2),
      transition: theme.transitions.create(["border-color", "border-width"], {
        duration: theme.transitions.duration.shortest,
      }),
    }),
  },
};

// ----------------------------------------------------------------------

const MuiFilledInput: Components<Theme>["MuiFilledInput"] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: { disableUnderline: true },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.shape.borderRadius,
      backgroundColor: varAlpha(theme.vars.palette.grey["500Channel"], 0.08),
      "&:hover": { backgroundColor: varAlpha(theme.vars.palette.grey["500Channel"], 0.16) },
      [`&.${filledInputClasses.focused}`]: {
        backgroundColor: varAlpha(theme.vars.palette.grey["500Channel"], 0.16),
      },
      [`&.${filledInputClasses.error}`]: {
        backgroundColor: INPUT_ERROR_BG,
        [`&.${filledInputClasses.focused}`]: {
          backgroundColor: INPUT_ERROR_BG_FOCUSED,
        },
      },
      [`&.${filledInputClasses.disabled}`]: {
        backgroundColor: theme.vars.palette.action.disabledBackground,
      },
    }),
  },
};

const MuiTextField: Components<Theme>["MuiTextField"] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: { variant: "outlined" },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {},
};

// ----------------------------------------------------------------------

export const textfield = {
  MuiInput,
  MuiInputBase,
  MuiFilledInput,
  MuiOutlinedInput,
  MuiTextField,
};
